import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Link,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { pullRequestService } from '../../services/pullRequestService';
import { isAuthenticated } from '../../services/authService';
import PullRequestStats from './PullRequestStats';
import PRClosureTimeChart from '../charts/PRClosureTimeChart';

/**
 * PullRequestsTab component for displaying and managing pull requests
 * @returns {JSX.Element} PullRequestsTab component
 */
const PullRequestsTab = () => {
  // State management
  const [selectedRepository, setSelectedRepository] = useState('');
  const [repositories, setRepositories] = useState([]);
  const [pullRequests, setPullRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [stateFilter, setStateFilter] = useState('all');
  const [sortField, setSortField] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showOnlyWithTests, setShowOnlyWithTests] = useState(false);

  // Helper function to check if a PR has tests
  const hasTests = (pr) => {
    return (
      (pr.impactedTestsId !== null && pr.impactedTestsId !== undefined) ||
      (pr.impactedTests && Array.isArray(pr.impactedTests) && pr.impactedTests.length > 0) ||
      (pr.impactedMethodsCount && pr.impactedMethodsCount > 0)
    );
  };

  // Helper function to calculate time to approve in hours
  const calculateTimeToApprove = (createdDate, approvedDate) => {
    if (!createdDate || !approvedDate) return null;
    
    const created = new Date(createdDate);
    const approved = new Date(approvedDate);
    const diffInMs = approved.getTime() - created.getTime();
    const diffInHours = Math.round(diffInMs / (1000 * 60 * 60) * 10) / 10; // Round to 1 decimal place
    
    return diffInHours;
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort pull requests based on current settings
  const filteredPullRequests = showOnlyWithTests
    ? pullRequests.filter(pr => hasTests(pr))
    : pullRequests;

  const sortedPullRequests = [...filteredPullRequests].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'prNumber':
        aValue = a.prNumber;
        bValue = b.prNumber;
        break;
      case 'title':
        aValue = a.title?.toLowerCase() || '';
        bValue = b.title?.toLowerCase() || '';
        break;
      case 'author':
        aValue = a.author?.toLowerCase() || '';
        bValue = b.author?.toLowerCase() || '';
        break;
      case 'state':
        aValue = a.state;
        bValue = b.state;
        break;
      case 'createdDate':
        aValue = new Date(a.createdDate);
        bValue = new Date(b.createdDate);
        break;
      case 'lastApprovedDate':
        aValue = a.lastApprovedDate ? new Date(a.lastApprovedDate) : new Date(0);
        bValue = b.lastApprovedDate ? new Date(b.lastApprovedDate) : new Date(0);
        break;
      case 'timeToApprove':
        aValue = calculateTimeToApprove(a.createdDate, a.lastApprovedDate) || 0;
        bValue = calculateTimeToApprove(b.createdDate, b.lastApprovedDate) || 0;
        break;
      case 'mergedDate':
        aValue = a.mergedDate ? new Date(a.mergedDate) : new Date(0);
        bValue = b.mergedDate ? new Date(b.mergedDate) : new Date(0);
        break;
      case 'filesChanged':
        aValue = a.filesChanged || 0;
        bValue = b.filesChanged || 0;
        break;
      case 'linesAdded':
        aValue = a.linesAdded || 0;
        bValue = b.linesAdded || 0;
        break;
      case 'linesDeleted':
        aValue = a.linesDeleted || 0;
        bValue = b.linesDeleted || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate the sorted data
  const paginatedPullRequests = sortedPullRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // Dialog state
  const [selectedPR, setSelectedPR] = useState(null);
  const [checksDialogOpen, setChecksDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  
  // Check if user is authenticated
  const userIsAuthenticated = isAuthenticated();

  // Load repositories on component mount
  useEffect(() => {
    loadRepositories();
  }, []);

  // Load PRs when repository changes
  useEffect(() => {
    if (selectedRepository) {
      loadPullRequests();
      loadStats();
    }
  }, [selectedRepository, stateFilter]);

  /**
   * Load available repositories
   */
  const loadRepositories = async () => {
    try {
      setLoading(true);
      const response = await pullRequestService.getRepositoriesWithPRs();
      console.log('Repositories response:', response);
      console.log('Number of repositories:', response.data?.length || 0);
      setRepositories(response.data || []);
      
      // Auto-select highq_collaborate if available, otherwise first repository
      if (response.data && response.data.length > 0) {
        const highqCollaborateRepo = response.data.find(repo => repo.githubRepoName === 'highq_collaborate');
        if (highqCollaborateRepo) {
          setSelectedRepository(highqCollaborateRepo.repositoryId);
          console.log('Auto-selected highq_collaborate repository');
        } else {
          setSelectedRepository(response.data[0].repositoryId);
          console.log('Auto-selected first repository:', response.data[0].githubRepoName);
        }
      }
    } catch (error) {
      console.error('Error loading repositories:', error);
      setError('Failed to load repositories');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load pull requests for selected repository
   */
  const loadPullRequests = async () => {
    try {
      setLoading(true);
      const filters = {
        state: stateFilter === 'all' ? undefined : stateFilter,
        page: 1, // Always fetch from first page
        limit: 1000, // Fetch all PRs (increased from 25 to handle 250+ PRs)
        sortBy: 'createdDate',
        sortOrder: 'desc'
      };
      
      const response = await pullRequestService.getPullRequestsByRepository(selectedRepository, filters);
      setPullRequests(response.data.pullRequests || []);
      console.log(`Loaded ${response.data.pullRequests?.length || 0} pull requests`);
    } catch (error) {
      console.error('Error loading pull requests:', error);
      setError('Failed to load pull requests');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load statistics for selected repository
   */
  const loadStats = async () => {
    try {
      const response = await pullRequestService.getPullRequestStats(selectedRepository);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  /**
   * Sync pull requests for selected repository
   */
  const handleSync = async (force = false) => {
    try {
      setSyncing(true);
      setError(null);
      setSuccessMessage(null);
      
      const response = await pullRequestService.syncPullRequests(selectedRepository, force);
      
      setSuccessMessage(`Successfully synced ${response.data.syncedCount} new PRs and updated ${response.data.updatedCount} existing PRs`);
      
      // Reload data after sync
      await Promise.all([loadPullRequests(), loadStats()]);
      
    } catch (error) {
      console.error('Error syncing pull requests:', error);
      setError('Failed to sync pull requests');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Handle repository selection change
   */
  const handleRepositoryChange = (event) => {
    setSelectedRepository(event.target.value);
    setPage(0); // Reset to first page
  };

  /**
   * Handle state filter change
   */
  const handleStateFilterChange = (event) => {
    setStateFilter(event.target.value);
    setPage(0); // Reset to first page
  };

  /**
   * Handle show only with tests toggle change
   */
  const handleShowOnlyWithTestsChange = (event) => {
    setShowOnlyWithTests(event.target.checked);
    setPage(0); // Reset to first page
  };

  /**
   * Handle page change
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Open checks dialog for a PR
   */
  const handleOpenChecksDialog = (pr) => {
    setSelectedPR(pr);
    setChecksDialogOpen(true);
  };

  /**
   * Close checks dialog
   */
  const handleCloseChecksDialog = () => {
    setChecksDialogOpen(false);
    setSelectedPR(null);
  };

  /**
   * Open logs dialog for a PR
   */
  const handleOpenLogsDialog = (pr) => {
    setSelectedPR(pr);
    setLogsDialogOpen(true);
  };

  /**
   * Close logs dialog
   */
  const handleCloseLogsDialog = () => {
    setLogsDialogOpen(false);
    setSelectedPR(null);
  };

  /**
   * Format log level for display
   */
  const getLogLevelChip = (level) => {
    const config = {
      log: { color: 'default', label: 'LOG' },
      info: { color: 'info', label: 'INFO' },
      warn: { color: 'warning', label: 'WARN' },
      error: { color: 'error', label: 'ERROR' },
      debug: { color: 'default', label: 'DEBUG' }
    };
    
    const { color, label } = config[level] || config.log;
    return <Chip label={label} color={color} size="small" sx={{ minWidth: 60 }} />;
  };

  /**
   * Format log timestamp
   */
  const formatLogTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  /**
   * Get status chip for PR state
   */
  const getStateChip = (state) => {
    const config = {
      open: { color: 'warning', icon: <ScheduleIcon />, label: 'Open' },
      closed: { color: 'default', icon: <CancelIcon />, label: 'Closed' },
      merged: { color: 'success', icon: <CheckCircleIcon />, label: 'Merged' }
    };
    
    const { color, icon, label } = config[state] || config.closed;
    return <Chip icon={icon} label={label} color={color} size="small" />;
  };

  /**
   * Get status chip for PR status
   */
  const getStatusChip = (status) => {
    const displayStatus = status || 'To be Executed';
    
    const config = {
      'To be Executed': { color: 'warning', icon: <ScheduleIcon />, label: 'To be Executed' },
      'Executed': { color: 'success', icon: <CheckCircleIcon />, label: 'Executed' },
      open: { color: 'warning', icon: <ScheduleIcon />, label: 'Open' },
      closed: { color: 'default', icon: <CancelIcon />, label: 'Closed' },
      merged: { color: 'success', icon: <CheckCircleIcon />, label: 'Merged' },
      active: { color: 'primary', icon: <ScheduleIcon />, label: 'Active' }
    };
    
    const { color, icon, label } = config[displayStatus] || { color: 'default', icon: null, label: displayStatus };
    return <Chip icon={icon} label={label} color={color} size="small" variant="outlined" />;
  };

  /**
   * Get status chip for check status
   */
  const getCheckStatusChip = (status) => {
    const config = {
      success: { color: 'success', icon: <CheckCircleIcon /> },
      failure: { color: 'error', icon: <CancelIcon /> },
      pending: { color: 'warning', icon: <ScheduleIcon /> },
      skipped: { color: 'default', icon: <InfoIcon /> },
      cancelled: { color: 'default', icon: <CancelIcon /> },
      neutral: { color: 'info', icon: <InfoIcon /> }
    };
    
    const { color, icon } = config[status] || config.pending;
    return <Chip icon={icon} label={status} color={color} size="small" />;
  };

  /**
   * Format date for display
   */
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = new Date(date);
    const dateStr = dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return (
      <Box>
        <Typography variant="body2" component="div">
          {dateStr}
        </Typography>
        <Typography variant="body2" component="div" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {timeStr}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header with Repository Selection and Sync */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Pull Requests
        </Typography>
        
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Repository</InputLabel>
            <Select
              value={selectedRepository}
              onChange={handleRepositoryChange}
              label="Repository"
            >
              {repositories.map((repo) => (
                <MenuItem key={repo.repositoryId} value={repo.repositoryId}>
                  {repo.githubRepoName} ({repo.prCount} PRs)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={() => handleSync(false)}
            disabled={!selectedRepository || syncing}
          >
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => handleSync(true)}
            disabled={!selectedRepository || syncing}
          >
            Force Sync
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

          {/* Statistics */}
          {selectedRepository && (
            <PullRequestStats stats={stats} loading={loading} />
          )}

          {/* PR Closure Time Chart */}
          <Box mb={3}>
            <PRClosureTimeChart />
          </Box>

      {/* Filters */}
      {selectedRepository && (
        <Box display="flex" gap={2} mb={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>State</InputLabel>
            <Select
              value={stateFilter}
              onChange={handleStateFilterChange}
              label="State"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyWithTests}
                onChange={handleShowOnlyWithTestsChange}
                color="primary"
              />
            }
            label="Show only PRs with tests"
          />
        </Box>
      )}

      {/* Pull Requests Table */}
      {selectedRepository ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('prNumber')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      PR #
                      {sortField === 'prNumber' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('title')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Title
                      {sortField === 'title' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('author')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Author
                      {sortField === 'author' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('state')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      PR State
                      {sortField === 'state' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('status')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Execution Status
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('createdDate')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Created
                      {sortField === 'createdDate' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('lastApprovedDate')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Approved
                      {sortField === 'lastApprovedDate' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('timeToApprove')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Time to Approve
                      {sortField === 'timeToApprove' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('mergedDate')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Merged
                      {sortField === 'mergedDate' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('filesChanged')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Files
                      {sortField === 'filesChanged' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('linesAdded')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Lines
                      {sortField === 'linesAdded' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Checks</TableCell>
                  {userIsAuthenticated && <TableCell>Logs</TableCell>}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={userIsAuthenticated ? 13 : 12} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : pullRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={userIsAuthenticated ? 13 : 12} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No pull requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPullRequests.map((pr) => (
                    <TableRow key={pr._id} hover>
                      <TableCell>
                        <Link 
                          href={pr.prId} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none' }}
                        >
                          #{pr.prNumber}
                          <OpenInNewIcon sx={{ ml: 1, fontSize: 16 }} />
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {pr.title}
                        </Typography>
                      </TableCell>
                      <TableCell>{pr.author}</TableCell>
                      <TableCell>{getStateChip(pr.state)}</TableCell>
                      <TableCell>{getStatusChip(pr.status)}</TableCell>
                      <TableCell>{formatDate(pr.createdDate)}</TableCell>
                      <TableCell>{formatDate(pr.lastApprovedDate)}</TableCell>
                      <TableCell>
                        {(() => {
                          const timeToApprove = calculateTimeToApprove(pr.createdDate, pr.lastApprovedDate);
                          if (timeToApprove === null) {
                            return (
                              <Typography variant="body2" color="textSecondary">
                                N/A
                              </Typography>
                            );
                          }
                          return (
                            <Typography variant="body2">
                              {timeToApprove}h
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{formatDate(pr.mergedDate)}</TableCell>
                      <TableCell>{pr.filesChanged}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          +{pr.linesAdded} / -{pr.linesDeleted}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {pr.checks && pr.checks.length > 0 ? (
                          <Tooltip title={`${pr.checks.filter(c => c.conclusion === 'success').length}/${pr.checks.length} checks passed`}>
                            <Chip
                              label={`${pr.checks.filter(c => c.conclusion === 'success').length}/${pr.checks.length}`}
                              color={pr.checks.some(c => c.conclusion === 'failure') ? 'error' : pr.checks.some(c => c.conclusion === 'pending') ? 'warning' : 'success'}
                              size="small"
                              onClick={() => handleOpenChecksDialog(pr)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">No Checks</Typography>
                        )}
                      </TableCell>
                      {userIsAuthenticated && (
                        <TableCell>
                          <Tooltip title="View Logs">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenLogsDialog(pr)}
                              disabled={!pr.logs || pr.logs.length === 0}
                            >
                              <DescriptionIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell>
                        <Tooltip title="View Checks">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenChecksDialog(pr)}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={sortedPullRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            Select a repository to view pull requests
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Data includes the last 250 PRs (PRs targeting default branch)
          </Typography>
        </Paper>
      )}

      {/* Checks Dialog */}
      <Dialog 
        open={checksDialogOpen} 
        onClose={handleCloseChecksDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Checks for PR #{selectedPR?.prNumber}
        </DialogTitle>
        <DialogContent>
          {selectedPR && (
            <List>
              {selectedPR.checks.map((check, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {getCheckStatusChip(check.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={check.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Status: {check.status} | Conclusion: {check.conclusion || 'N/A'}
                        </Typography>
                        {check.completedAt && (
                          <Typography variant="body2" color="textSecondary">
                            Completed: {formatDate(check.completedAt)}
                          </Typography>
                        )}
                        {check.detailsUrl && (
                          <Link href={check.detailsUrl} target="_blank" rel="noopener noreferrer">
                            View Details
                          </Link>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChecksDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog 
        open={logsDialogOpen} 
        onClose={handleCloseLogsDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Background Logs for PR #{selectedPR?.prNumber}
        </DialogTitle>
        <DialogContent>
          {selectedPR && (
            <Box>
              {!selectedPR.logs || selectedPR.logs.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                  No logs available for this pull request.
                </Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Total logs: {selectedPR.logs.length}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 180 }}>Timestamp</TableCell>
                          <TableCell sx={{ width: 100 }}>Level</TableCell>
                          <TableCell>Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPR.logs.map((log, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {formatLogTimestamp(log.timestamp)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {getLogLevelChip(log.level)}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {log.message}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PullRequestsTab;
